import { isBrowser, loadBundle } from '@okapi/dom-utils';
import { emit } from '@okapi/pubsub';
import { pathToRegexp } from '@okapi/router-regexp';
import { getSharedConfig, mainBundleConfig, parseRawBundleConfig, } from '@okapi/store-config';
import { createPath, parsePath } from './path';
const events = ['popstate', 'pushState', 'replaceState', 'hashchange'];
export const defaultBundle = 'defaultBundle';
export class GlobalRouter {
    constructor(url) {
        this._currentPath = '/';
        this._fullPath = { path: '/' };
        this._redirect = null;
        this._bundle = defaultBundle;
        /**
         * Обьект текущего пути
         */
        this.toJSON = () => ({
            bundle: this._bundle,
            path: this.getPath(),
            params: this.getParams(),
            search: this.getSearch(),
            hash: this.getHash(),
        });
        /**
         * Переход на новую страницу
         * @param {string} to адрес новой страницы
         * @param {boolean} replace заменить старую страницу текущей
         */
        this.navigate = (to, replace = false) => {
            let path = '';
            switch (to[0]) {
                case '?':
                    path += this.getPath() + (to.length > 1 ? to : '');
                    break;
                case '#':
                    path += this.getPath() + this.getSearch() + (to.length > 1 ? to : '');
                    break;
                default:
                    path += to;
                    break;
            }
            if (isBrowser()) {
                history[replace ? 'replaceState' : 'pushState'](0, '', path);
                return path;
            }
            this._redirect = parsePath(path);
            return path;
        };
        /**
         * Проверяет соответсвие пути всем имеющимся паттернам
         * @param {string} path Путь
         * @param {boolean} strict Если true, то путь с косой чертой в конце будет соответсвовать только пути с косой чертой в конце
         * @param {boolean} exact Когда true, будет соответствовать только в том случае, если путь точно совпадает.
         */
        this.checkPath = (path, strict, exact) => {
            return this.matcher(this._availablePaths, path, strict, exact);
        };
        /**
         * Проверяет соответсвие пути заданному паттерну или массиву паттернов
         * @param {string|string[]} pattern Валидный паттерн пути
         * @param {string} path Путь
         * @param {boolean} strict Если true, то путь с косой чертой в конце будет соответсвовать только пути с косой чертой в конце
         * @param {boolean} exact Когда true, будет соответствовать только в том случае, если путь точно совпадает.
         */
        this.matcher = (pattern, path, strict, exact) => {
            const patterns = Array.isArray(pattern) ? pattern : [pattern];
            return patterns.reduce((memo, localPattern) => {
                const { regexp, keys } = this.getRegexp(localPattern || '', strict, exact);
                const out = regexp.exec(path);
                if (!out) {
                    return memo;
                }
                const params = keys.reduce((paramsMemo, key, i) => {
                    paramsMemo[key.name] = out[i + 1];
                    return paramsMemo;
                }, memo[1] || {});
                return [true, params];
            }, [false, null]);
        };
        /**
         * Метод проверки изменения пути
         * 1. проверяем наличие пути в уже загруженных бандлах
         * 2. если нет локально, берем информацию о бандле с SD сервера
         * 2.1. получаем конфиг микрофронта
         * 2.2. подключаем бандл
         *
         * Оповезаем подписчиков о изменении пути
         * Перезагружаем страницу, если информации о микрофронте нет в SD сервере
         */
        this.checkForUpdates = () => {
            const path = this.getPath();
            if (path !== this._currentPath) {
                const paths = Array.from(this._paths.values());
                for (let i = 0; i < paths.length; i += 1) {
                    if (paths[i].regexp.exec(path)) {
                        return this.runChange(paths[i].name);
                    }
                }
                if (this._bundle === defaultBundle) {
                    this._paths.set(path, {
                        name: defaultBundle,
                        ...this.getRegexp(path, false, true),
                    });
                    return this.runChange(defaultBundle);
                }
                const sharedConfig = getSharedConfig();
                const server = sharedConfig.getServerByPath(path);
                if (server) {
                    return fetch(`${server}config`, { method: 'POST' })
                        .then(res => res.json())
                        .then(parseRawBundleConfig)
                        .then(bundle => loadBundle(bundle).then(() => bundle))
                        .then(this.addBundle)
                        .then(bundle => this.runChange(bundle.name));
                }
                if (isBrowser()) {
                    return location.replace(this.toString());
                }
            }
            return this.runChange(this._bundle);
        };
        /**
         * Подписка на основные события в window
         */
        this.subscribe = () => {
            events.forEach(e => addEventListener(e, this.checkForUpdates));
        };
        /**
         * Отписка на основные события в window
         */
        this.unsubscribe = () => {
            events.forEach(e => removeEventListener(e, this.checkForUpdates));
        };
        /**
         * Получить RegExp паттерна
         * @param pattern
         * @param strict
         * @param exact
         */
        this.getRegexp = (pattern, strict, exact) => {
            const key = `_${strict ? 't' : 'f'}_${exact ? 't' : 'f'}_${pattern}`;
            if (!this._cache.has(key)) {
                this._cache.set(key, pathToRegexp(pattern, strict, exact));
            }
            return this._cache.get(key);
        };
        /**
         * Добавить бандл микрофронта в кеш
         * @param bundle
         */
        this.addBundle = (bundle) => {
            this._bundles.set(bundle.name, bundle);
            const stored = (bundle && bundle.stored) || [];
            this._availablePaths = [
                ...new Set(stored.reduce((memo, curr) => memo.concat(curr[1]), this._availablePaths)),
            ];
            bundle.paths.forEach(path => {
                this._paths.set(path, {
                    name: bundle.name,
                    ...this.getRegexp(path, false, true),
                });
            });
            return bundle;
        };
        this._paths = new Map();
        this._cache = new Map();
        this._bundles = new Map();
        this._availablePaths = [];
        this._patched = false;
        if (isBrowser()) {
            const config = mainBundleConfig();
            this._currentPath = this.getPath();
            this._bundle = config.name || defaultBundle;
            this.addBundle(config);
            this.patchHistoryEvents();
            this.subscribe();
        }
        else {
            if (url) {
                this.fullPath = url;
            }
        }
    }
    set fullPath(path) {
        this._fullPath = parsePath(path);
    }
    get fullPath() {
        return createPath(this._fullPath);
    }
    get redirectURL() {
        if (!this._redirect) {
            return false;
        }
        return createPath(this._redirect);
    }
    /**
     * Текущий путь страницы (location.pathname)
     */
    getPath() {
        if (isBrowser()) {
            return location.pathname;
        }
        return this._fullPath.path || '/';
    }
    /**
     * Текущее значение location.search
     */
    getSearch() {
        if (isBrowser()) {
            return location.search;
        }
        return this._fullPath.search || '';
    }
    /**
     * Текущее значение location.hash
     */
    getHash() {
        if (isBrowser()) {
            return location.hash;
        }
        return this._fullPath.hash || '';
    }
    /**
     * Получить бандл по имени
     * @param {string} name
     * @return {MainBundleConfig | undefined}
     */
    getBundle(name) {
        return this._bundles.get(name);
    }
    /**
     * Текущее значение location.search в виде обьекта
     */
    getSearchParsed() {
        const locationSearch = new URLSearchParams(this.getSearch());
        const search = {};
        locationSearch.forEach((value, key) => {
            search[key] = value;
        });
        return search;
    }
    /**
     * Параметры для текущего пути
     */
    getParams() {
        let params = {};
        const paths = [...Array.from(this._paths.values()), ...Array.from(this._cache.values())];
        const path = this.getPath();
        for (let i = 0; i < paths.length; i += 1) {
            const out = paths[i].regexp.exec(path);
            if (out) {
                params = paths[i].keys.reduce((memo, key, index) => {
                    memo[key.name] = out[index + 1];
                    return memo;
                }, params);
            }
        }
        return params;
    }
    /**
     * Текущий путь строкой
     */
    toString() {
        return this.getPath() + this.getSearch() + this.getHash();
    }
    /**
     * Патчим базовые методы history, чтобы роутер мог перехватывать события
     */
    patchHistoryEvents() {
        if (this._patched) {
            return;
        }
        this._patched = true;
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const _self = this;
        const originalPushState = history.pushState;
        const originalReplaceState = history.replaceState;
        history.pushState = function pushState(...args) {
            const result = originalPushState.apply(this, args);
            _self.checkForUpdates();
            return result;
        };
        history.replaceState = function replaceState(...args) {
            const result = originalReplaceState.apply(this, args);
            _self.checkForUpdates();
            return result;
        };
    }
    /**
     * Оповещаем подписчиков об изменении пути
     * @param bundleName
     */
    runChange(bundleName) {
        this._bundle = bundleName;
        const data = this.toJSON();
        this._currentPath = data.path;
        emit('changePath', data);
    }
}
